import { useCallback, useEffect, useState } from "react";
import { Bug, Check, Lightbulb, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/api";
import { useI18n } from "@/i18n";

const mono = "font-mono2 text-[10px] uppercase tracking-wider";

export default function FeedbackPanel() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [notify, setNotify] = useState("");
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("new");

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/feedback?limit=200");
      setRows(r.data.feedback || []);
      setNotify(r.data.notify_to || "");
      setCounts(r.data.counts || {});
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (row, status) => {
    try {
      await api.patch(`/admin/feedback/${row.id}`, { status });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (row) => {
    if (!window.confirm(t("admin.fb_delete_confirm"))) return;
    try {
      await api.delete(`/admin/feedback/${row.id}`);
      toast.success(t("admin.fb_deleted"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const shown = rows.filter((r) => (filter === "all" ? true : r.status === filter));

  return (
    <div className="mb-6 rounded-xl p-5 hud-card" data-testid="feedback-panel">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-xl font-bold uppercase tracking-tight text-slate-100">
          {t("admin.feedback_title")}
          <span className="ml-2 font-mono2 text-xs text-[#00FF87]">{counts.new ?? 0} {t("admin.fb_new")}</span>
        </h2>
        <span className="flex gap-2">
          {["new", "done", "all"].map((f) => (
            <button key={f} data-testid={`fb-filter-${f}`} onClick={() => setFilter(f)}
              className={`${mono} rounded-md border px-2.5 py-1 ${filter === f ? "border-[#00FF87] text-[#00FF87]" : "border-[#1F293D] text-slate-400 hover:text-slate-200"}`}>
              {t(`admin.fb_${f}`)}
            </button>
          ))}
        </span>
      </div>
      <p className="mb-3 text-[11px] text-slate-500">{t("admin.fb_notify", { email: notify })}</p>

      <div className="space-y-2">
        {shown.length === 0 && <p className="text-xs text-slate-500" data-testid="fb-empty">{t("admin.fb_empty")}</p>}
        {shown.map((r) => (
          <div key={r.id} className="rounded-lg border border-[#1F293D] p-3" data-testid={`fb-row-${r.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`${mono} flex items-center gap-1 font-bold ${r.kind === "bug" ? "text-red-400" : "text-[#00F0FF]"}`}>
                {r.kind === "bug" ? <Bug className="h-3 w-3" /> : <Lightbulb className="h-3 w-3" />}
                {t(`feedback.kind_${r.kind}`)}
              </span>
              <span className={`${mono} text-slate-500`}>{(r.created_at || "").slice(0, 16).replace("T", " ")}</span>
              {r.page && <span className={`${mono} text-slate-500`}>{r.page}</span>}
              {r.email && <a href={`mailto:${r.email}`} className={`${mono} text-slate-300 underline`}>{r.email}</a>}
              <span className={`${mono} ml-auto font-bold ${r.status === "new" ? "text-[#FFD600]" : "text-slate-500"}`}>
                {t(`admin.fb_${r.status}`)}
              </span>
              <button
                data-testid={`fb-toggle-${r.id}`}
                onClick={() => setStatus(r, r.status === "new" ? "done" : "new")}
                title={r.status === "new" ? t("admin.fb_mark_done") : t("admin.fb_mark_new")}
                className="rounded border border-[#1F293D] p-1 text-slate-400 hover:border-[#00FF87]/60 hover:text-[#00FF87]"
              >
                {r.status === "new" ? <Check className="h-3 w-3" /> : <RotateCcw className="h-3 w-3" />}
              </button>
              {filter === "all" && (
                <button
                  data-testid={`fb-delete-${r.id}`}
                  onClick={() => remove(r)}
                  title={t("admin.fb_delete")}
                  className="rounded border border-[#1F293D] p-1 text-slate-400 hover:border-red-500/60 hover:text-red-400"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              )}
            </div>
            <p className="mt-2 whitespace-pre-wrap text-xs leading-relaxed text-slate-300">{r.message}</p>
            {r.handled_by && <p className={`${mono} mt-1 text-slate-600`}>{r.handled_by} · {(r.handled_at || "").slice(0, 16).replace("T", " ")}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
